import { useDispatch } from 'react-redux';

import { authenticatedJsonFetch } from '@perpay-web/authentication/authenticatedJsonFetch';
import { authentication } from '@perpay-web/authentication/singleton';
import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import { useDataModule } from '@perpay-web/data-module/useDataModule';
import { useReduxDataModuleHookConnection } from '@perpay-web/data-module/useReduxDataModuleHookConnection';
import { analyticsApplicationSubmitted } from '@perpay-web/fintech/actions/analytics/checkout';
import {
    checkoutUpdateOrder,
    checkoutUpdateOrderError,
    checkoutUpdateOrderReset,
    checkoutUpdateOrderSuccess,
} from '@perpay-web/fintech/actions/entities/checkoutUpdateOrder';
import { CHECKOUT_CONFIRM_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';
import { centsToDollar } from '@perpay-web/utils/numberUtils';

const useCheckoutUpdateOrderDataModule = () => {
    const dispatch = useDispatch();
    const dataModule = useDataModule(({ payload, checkoutUuid, revenue }) =>
        authenticatedJsonFetch(
            CHECKOUT_CONFIRM_ENDPOINT.replace(':uuid', checkoutUuid),
            payload,
            HTTPMethod.post,
        ).then(() => ({
            order_uuid: checkoutUuid,
            revenue: revenue ? centsToDollar(revenue) : 0,
        })),
    );

    useReduxDataModuleHookConnection({
        dataModule,
        hookAction$,
        request: checkoutUpdateOrder,
        error: checkoutUpdateOrderError,
        success: checkoutUpdateOrderSuccess,
        reset: checkoutUpdateOrderReset,
    });

    useActionObservableCallback(
        hookAction$,
        checkoutUpdateOrderSuccess().type,
        (response) =>
            dispatch(
                analyticsApplicationSubmitted({
                    user_uuid: authentication.getUserUuid(),
                    order_uuid: response.order_uuid,
                    amount: response.revenue,
                }),
            ),
    );

    return dataModule;
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const dataModule = useCheckoutUpdateOrderDataModule();

        return (
            <Context.Provider value={dataModule}>{children}</Context.Provider>
        );
    };

    return Provider;
};

export const [
    UseCheckoutUpdateOrderDataModuleProvider,
    useCheckoutUpdateOrderContext,
] = createContextProvider(providerFactory);
